import React from 'react';
import ApolloError from '@shared/components/ApolloError';
import useATLEventBidding from '../../hooks/useATLEventBidding';
import { Accordion, AccordionDetails, AccordionSummary, Checkbox } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import StyledTable from '@shared/components/StyledTable';
import moment from 'moment';
import { useSelectedDateStore } from '../../CalendarStore';
import CalendarEventMapper from '../../mappers/CalendarEventMapper';
import CheckedXIcon from '@shared/components/CheckedXIcon';

const TABLE_FIELDS = {
  BID: {
    title: 'Bid',
    dataIndex: 'eventBidDate'
  },
  ORG_UNIT_NAMES: {
    title: 'Organizational Units',
    dataIndex: 'orgUnitNames'
  }
};

/**
 * @typedef {object} OpenForBidEventsTableProps
 * @property {Array<*>} [openForATLBidEventData]
 * @property {boolean} [loading]
 */

/**
 * @param {OpenForBidEventsTableProps} props
 */
const OpenForBidEventsTable = props => {
  const { openForATLBidEventData, loading } = props;

  const openForATLBidEvents =
    CalendarEventMapper.toOpenForATLBidEventsTable(openForATLBidEventData);

  const selectedDate = useSelectedDateStore(state => state.selectedDate);

  const { handleBidOnEvent, handleRemoveBid, eventBidError } = useATLEventBidding();

  const startOfMonth = moment(selectedDate).startOf('month');
  const endOfMonth = moment(selectedDate).endOf('month');

  const openForATLBidEventsWithinMonth = openForATLBidEvents.filter(
    x =>
      moment(x.startDate).isSameOrAfter(startOfMonth) &&
      moment(x.startDate).isSameOrBefore(endOfMonth)
  );

  const columns = [
    {
      title: TABLE_FIELDS.BID.title,
      dataIndex: TABLE_FIELDS.BID.dataIndex,
      width: '1rem',
      render: (_, record) => (
        <Checkbox
          size='small'
          color='default'
          checked={!!record.eventBidDate}
          checkedIcon={<CheckedXIcon size='small' />}
          onChange={e => handleCheckboxOnChange(record.id, e.target.checked)}
        />
      )
    },
    {
      title: TABLE_FIELDS.ORG_UNIT_NAMES.title,
      dataIndex: TABLE_FIELDS.ORG_UNIT_NAMES.dataIndex
    }
  ];

  const handleCheckboxOnChange = async (eventId, checked) => {
    checked ? await handleBidOnEvent(eventId) : await handleRemoveBid(eventId);
  };

  if (eventBidError) {
    return <ApolloError />;
  }

  return (
    <Accordion
      data-testid='open-for-atl-bid-events-section'
      className='open-for-atl-bid-events-section'
      slotProps={{
        transition: { unmountOnExit: true }
      }}
      defaultExpanded>
      <AccordionSummary expandIcon={<ExpandMoreIcon data-testid='expand-icon' />}>
        <h3>Open for ATL Bid Events</h3>
      </AccordionSummary>
      <AccordionDetails>
        <StyledTable
          rowKey='id'
          dataSource={openForATLBidEventsWithinMonth}
          columns={columns}
          pagination={false}
          showSorterTooltip={false}
          loading={loading}
        />
      </AccordionDetails>
    </Accordion>
  );
};

export default OpenForBidEventsTable;
